import { createAsyncThunk } from '@reduxjs/toolkit';
import {
    getAllCustomerFormSubmissions,
    getCustomerFormSubmissionById,
    updateCustomerFormSubmission,
    deleteCustomerFormSubmission,
    getAllFormSubmissionTypes,
    createFormSubmissionType,
    deleteFormSubmissionType,
    normalizeCustomerFormSubmission,
} from '../../api/customerFormSubmissions';
import store from '../../store';
import { setNotifications } from '../slices/notifications';

const handleThunkError = (error: any, rejectWithValue: (value: unknown) => any) => {
    store.dispatch(setNotifications({ text: error.message, type: 'error' }));
    return rejectWithValue(error.message);
};

export const getCustomerFormSubmissions = createAsyncThunk(
    'customerFormSubmissions/getCustomerFormSubmissions',
    async (queryParams: any, { rejectWithValue }) => {
        try {
            return await getAllCustomerFormSubmissions(queryParams);
        } catch (error: any) {
            return handleThunkError(error, rejectWithValue);
        }
    }
);

export const getCustomerFormSubmission = createAsyncThunk(
    'customerFormSubmissions/getCustomerFormSubmission',
    async (submissionId: number | string, { rejectWithValue }) => {
        try {
            return await getCustomerFormSubmissionById(submissionId);
        } catch (error: any) {
            return handleThunkError(error, rejectWithValue);
        }
    }
);

export const patchCustomerFormSubmission = createAsyncThunk(
    'customerFormSubmissions/patchCustomerFormSubmission',
    async (
        { submissionId, processed }: { submissionId: number | string; processed: boolean },
        { rejectWithValue }
    ) => {
        try {
            const result = await updateCustomerFormSubmission(submissionId, { processed });
            store.dispatch(setNotifications({
                text: processed ? 'Submission marked as processed' : 'Submission marked as unprocessed',
                type: 'success',
            }));
            const updated = normalizeCustomerFormSubmission(result);
            return {
                submissionId,
                processed: typeof updated?.processed === 'boolean' ? updated.processed : processed,
            };
        } catch (error: any) {
            return handleThunkError(error, rejectWithValue);
        }
    }
);

export const removeCustomerFormSubmission = createAsyncThunk(
    'customerFormSubmissions/removeCustomerFormSubmission',
    async (submissionId: number | string, { rejectWithValue }) => {
        try {
            await deleteCustomerFormSubmission(submissionId);
            store.dispatch(setNotifications({ text: 'Submission deleted', type: 'success' }));
            return submissionId;
        } catch (error: any) {
            return handleThunkError(error, rejectWithValue);
        }
    }
);

export const getFormSubmissionTypes = createAsyncThunk(
    'customerFormSubmissions/getFormSubmissionTypes',
    async (_, { rejectWithValue }) => {
        try {
            return await getAllFormSubmissionTypes();
        } catch (error: any) {
            return handleThunkError(error, rejectWithValue);
        }
    }
);

export const createFormSubmissionTypeThunk = createAsyncThunk(
    'customerFormSubmissions/createFormSubmissionType',
    async (data: { id: number; label?: string }, { rejectWithValue }) => {
        try {
            const result = await createFormSubmissionType(data);
            store.dispatch(setNotifications({ text: 'Form type created', type: 'success' }));
            return result;
        } catch (error: any) {
            return handleThunkError(error, rejectWithValue);
        }
    }
);

export const removeFormSubmissionType = createAsyncThunk(
    'customerFormSubmissions/removeFormSubmissionType',
    async (typeId: number | string, { rejectWithValue }) => {
        try {
            await deleteFormSubmissionType(typeId);
            store.dispatch(setNotifications({ text: 'Form type deleted', type: 'success' }));
            return typeId;
        } catch (error: any) {
            return handleThunkError(error, rejectWithValue);
        }
    }
);
